import type { ComponentChildren } from "preact";
import { useCallback, useEffect, useRef, useState } from "react";

const calm = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * A row of tabs that scrolls sideways on narrow screens. A clipped word is easy to miss,
 * so an arrow marks that more tabs exist, and the active tab is brought into view.
 */
export function ScrollRow({
	label,
	active,
	className = "",
	children,
}: {
	label: string;
	/** Changes when the selected tab does. */
	active?: unknown;
	className?: string;
	children: ComponentChildren;
}) {
	const row = useRef<HTMLElement>(null);
	const [more, setMore] = useState(false);
	const measure = useCallback(() => {
		const n = row.current;
		if (n) setMore(n.scrollLeft + n.clientWidth < n.scrollWidth - 4);
	}, []);

	useEffect(() => {
		const n = row.current;
		if (!n) return;
		const observer = new ResizeObserver(measure);
		observer.observe(n);
		return () => observer.disconnect();
	}, [measure]);

	useEffect(() => {
		const n = row.current;
		const tab = n?.querySelector<HTMLElement>('[aria-current="page"]');
		if (n && tab) n.scrollLeft = tab.offsetLeft - n.offsetLeft - (n.clientWidth - tab.offsetWidth) / 2;
		measure();
	}, [active, measure]);

	return (
		<div className="relative min-w-0">
			<nav ref={row} aria-label={label} onScroll={measure} className={`no-scrollbar flex gap-px overflow-x-auto ${className}`}>
				{children}
			</nav>
			{more ? (
				<button
					type="button"
					aria-label={`More in ${label}`}
					onClick={() => row.current?.scrollBy({ left: row.current.clientWidth * 0.6, behavior: calm() ? "auto" : "smooth" })}
					className="absolute inset-y-0 right-0 w-9 border-l border-line bg-ink text-title text-fg"
				>
					›
				</button>
			) : null}
		</div>
	);
}
